import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Instagram, Images, CalendarDays, ArrowUpRight } from "lucide-react";
import { api, previewUrl } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { EventCard } from "@/pages/Events";
import { PhotographerAvatar } from "@/components/PhotographerAvatar";

const FeaturedCover = ({ photo }) => (
  <Link to={`/events/${photo.event_id}`} className="group relative block mb-14 rounded-3xl overflow-hidden bg-zinc-100 fade-up" data-testid="studio-featured-photo">
    <img src={previewUrl(photo.id)} alt={photo.event_title} className="w-full aspect-[4/3] sm:aspect-[21/9] object-cover transition-transform duration-700 group-hover:scale-[1.02]" />
    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 bg-gradient-to-t from-black/70 to-transparent flex items-end justify-between gap-4">
      <div className="min-w-0 text-white">
        <p className="text-[11px] uppercase tracking-widest font-bold text-white/70">Foto pilihan</p>
        <p className="font-bold text-lg sm:text-xl truncate" data-testid="studio-featured-event">{photo.event_title}</p>
      </div>
      <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-white text-zinc-900 text-sm font-semibold px-4 py-2 transition-transform group-hover:-translate-y-0.5">Lihat event<ArrowUpRight className="h-4 w-4" /></span>
    </div>
  </Link>
);


export function StudioList() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/photographers").then((r) => setList(r.data)); }, []);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <PageHeader label="Komunitas" title="Fotografer" />
      {list === null ? <p className="text-zinc-500">Memuat...</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="studio-list">
          {list.map((p) => (
            <Link key={p.id} to={`/studio/${p.id}`} className="rounded-2xl bg-white border border-zinc-200 p-6 flex gap-4 hover:border-zinc-300 hover:-translate-y-0.5 transition-[transform,border-color]" data-testid={`studio-card-${p.id}`}>
              <PhotographerAvatar id={p.id} name={p.name} version={p.avatar_version} testId={`studio-card-avatar-${p.id}`} />
              <div className="min-w-0">
                <p className="font-bold text-lg truncate">{p.name}</p>
                {p.city && <p className="text-sm text-zinc-500 inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.city}</p>}
                <p className="text-xs text-zinc-500 mt-2">{p.event_count} event · {p.photo_count} foto</p>
              </div>
            </Link>
          ))}
          {list.length === 0 && <p className="text-zinc-500">Belum ada fotografer.</p>}
        </div>
      )}
    </main>
  );
}

export default function StudioProfile() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/photographers/${id}`).then((r) => setD(r.data)).catch(() => setD(false)); }, [id]);

  if (d === null) return <p className="p-16 text-zinc-500">Memuat...</p>;
  if (!d) return <p className="p-16 text-zinc-500" data-testid="studio-not-found">Fotografer tidak ditemukan.</p>;
  const { photographer: p, events, featured } = d;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="studio-profile">
      <section className="flex flex-col sm:flex-row gap-6 sm:items-center mb-10 fade-up">
        <PhotographerAvatar id={p.id} name={p.name} version={p.avatar_version} size="h-20 w-20 sm:h-24 sm:w-24 text-2xl" testId="studio-avatar" />
        <div className="flex-1">
          <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold mb-1">Fotografer</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="studio-name">{p.name}</h1>
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-zinc-500">
            {p.city && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{p.city}</span>}
            {p.instagram && (
              <a href={`https://instagram.com/${p.instagram.replace("@", "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-blue-600" data-testid="studio-instagram-link">
                <Instagram className="h-4 w-4" />@{p.instagram.replace("@", "")}
              </a>
            )}
            <span className="inline-flex items-center gap-1"><CalendarDays className="h-4 w-4" />{p.event_count} event</span>
            <span className="inline-flex items-center gap-1"><Images className="h-4 w-4" />{p.photo_count} foto</span>
          </div>
          {p.bio && <p className="mt-4 text-zinc-600 max-w-2xl leading-relaxed" data-testid="studio-bio">{p.bio}</p>}
        </div>
      </section>
      {featured ? <FeaturedCover photo={featured} /> : <p className="mb-14 text-zinc-500" data-testid="studio-featured-empty">Belum ada foto.</p>}
      <h2 className="text-lg font-bold mb-5">Event</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="studio-events">
        {events.map((e) => <EventCard key={e.id} event={e} />)}
        {events.length === 0 && <p className="text-zinc-500">Belum ada event.</p>}
      </div>
    </main>
  );
}
